/**
 * 仓内作业：分时作业量折线 + 下方三个 tab（区间作业量与速率 / 账号作业明细 / 分拣机），移植产品原型 page.tsx 的「仓内作业」区。
 *
 * 在折线图上拖选的小时区间，就是下方表格统计的区间——先在曲线上看出哪几个小时掉下来，再直接反查那几个小时的作业。
 * 小时按 hub 当地时间切分（原型写死美西，见 lib/format.ts 的 hubHourKey）。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { C, OPS_KINDS } from '../constants';
import type { OpsAcctRow, OpsHourRow, SorterDevice, SorterHourRow } from '../types';
import { hubHourKey } from '../lib/format';
import { useFmt } from '../lib/useFmt';
import { I18N } from '../lib/useLabels';
import AccountTable from './AccountTable';
import OpsChart, { type Sel } from './OpsChart';
import RateTable from './RateTable';
import SorterPanel from './SorterPanel';

type OpsTab = 'rate' | 'acct' | 'sorter';

interface Props {
    hub: string;
    /** hub 当地时区（IANA） */
    timeZone: string;
    /** '' = 全部客户 */
    customer: string;
    now: number;
    opsHours: OpsHourRow[];
    opsAccounts: OpsAcctRow[];
    sorterHours: SorterHourRow[];
    sorterDevices: SorterDevice[];
}

const OpsSection: React.FC<Props> = ({ hub, timeZone, customer, now, opsHours, opsAccounts, sorterHours, sorterDevices }) => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const [windowHours, setWindowHours] = React.useState<24 | 48>(24);
    const [sel, setSel] = React.useState<Sel>(null); // 折线图圈选的小时区间（下标）
    const [opsTab, setOpsTab] = React.useState<OpsTab>('rate');

    // 当前仓的每小时序列（hub 当地时间）
    const hourLabels = React.useMemo(() => {
        const out: string[] = [];
        for (let i = windowHours - 1; i >= 0; i--) out.push(hubHourKey(now - i * 3_600_000, timeZone));
        return out;
    }, [now, windowHours, timeZone]);
    // 每小时、每类作业的 包裹数/包牌袋数/是否人工，按 类型|小时|人工 索引
    const opsIndex = React.useMemo(() => {
        const m = new Map<string, { cnt: number; bags: number }>();
        for (const r of opsHours) {
            if (r.hub !== hub || (customer && r.customer !== customer)) continue;
            const key = `${r.kind}|${r.hourKey}|${r.manual}`;
            const prev = m.get(key);
            m.set(key, { cnt: (prev?.cnt ?? 0) + r.count, bags: (prev?.bags ?? 0) + r.bags });
        }
        return m;
    }, [opsHours, hub, customer]);
    const hourSeries = React.useMemo(
        () =>
            OPS_KINDS.map((k) => ({
                k: k.k,
                color: k.color,
                label: t(`${I18N}.opsKinds.${k.k}`) ?? '',
                values: hourLabels.map(
                    (h) => (opsIndex.get(`${k.k}|${h}|0`)?.cnt ?? 0) + (opsIndex.get(`${k.k}|${h}|1`)?.cnt ?? 0)
                )
            })),
        [opsIndex, hourLabels, t]
    );
    // 圈选区间（没圈选 = 整个时间窗）
    const range = React.useMemo(() => {
        const n = hourLabels.length;
        const i0 = sel ? Math.max(0, Math.min(sel.i0, sel.i1)) : 0;
        const i1 = sel ? Math.min(n - 1, Math.max(sel.i0, sel.i1)) : n - 1;
        return { i0, i1, labels: hourLabels.slice(i0, i1 + 1) };
    }, [sel, hourLabels]);

    const tabs: [OpsTab, string][] = [
        ['rate', t(`${I18N}.ops.tabRate`) ?? ''],
        ['acct', t(`${I18N}.ops.tabAcct`) ?? ''],
        ['sorter', t(`${I18N}.ops.tabSorter`) ?? '']
    ];

    return (
        <div className="rounded-md bg-white" style={{ border: `1px solid ${C.border}` }}>
            <div
                className="flex flex-wrap items-baseline gap-3 rounded-t-md px-3 py-2.5"
                style={{ background: C.primaryBg, borderBottom: `1px solid ${C.border}` }}
            >
                <div
                    className="text-[17px] font-semibold"
                    style={{ color: C.text1, borderLeft: `3px solid ${C.primary}`, paddingLeft: 8 }}
                >
                    {t(`${I18N}.ops.title`)}
                </div>
                <span className="text-[12.5px]" style={{ color: C.text3 }}>
                    {t(`${I18N}.ops.subtitle`, { hub })}
                </span>
                <span className="flex-1" />
                <div className="flex overflow-hidden rounded" style={{ border: `1px solid ${C.border}` }}>
                    {([24, 48] as const).map((h) => (
                        <button
                            key={h}
                            type="button"
                            onClick={() => {
                                setWindowHours(h);
                                setSel(null);
                            }}
                            className="cursor-pointer border-0 text-[12.5px]"
                            style={{
                                padding: '4px 10px',
                                background: windowHours === h ? C.primary : '#fff',
                                color: windowHours === h ? '#fff' : C.text1
                            }}
                        >
                            {t(`${I18N}.ops.lastHours`, { h })}
                        </button>
                    ))}
                </div>
            </div>
            <div className="p-3">
                <OpsChart series={hourSeries} labels={hourLabels} sel={sel} onSelect={setSel} />
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: C.text3 }}>
                    <span>{t(`${I18N}.ops.chartHint`)}</span>
                    {sel ? (
                        <>
                            <span style={{ color: C.primary, fontWeight: 600 }}>
                                {t(`${I18N}.ops.selected`, {
                                    a: fmt.hourText(range.labels[0]),
                                    b: fmt.hourText(range.labels[range.labels.length - 1]),
                                    n: range.labels.length
                                })}
                            </span>
                            <button
                                type="button"
                                onClick={() => setSel(null)}
                                className="cursor-pointer rounded"
                                style={{ padding: '2px 8px', border: `1px solid ${C.border}`, color: C.text1, background: '#fff' }}
                            >
                                {t(`${I18N}.ops.clearSel`)}
                            </button>
                        </>
                    ) : (
                        <span>{t(`${I18N}.ops.wholeWindow`, { n: hourLabels.length })}</span>
                    )}
                </div>

                <div className="mt-3 flex gap-2">
                    {tabs.map(([k, label]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setOpsTab(k)}
                            className="cursor-pointer rounded text-[13px]"
                            style={{
                                padding: '6px 14px',
                                border: `1px solid ${opsTab === k ? C.primary : C.border}`,
                                background: opsTab === k ? C.primaryBg : '#fff',
                                color: opsTab === k ? C.primary : C.text2,
                                fontWeight: opsTab === k ? 600 : 400
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {opsTab === 'rate' ? (
                    <RateTable labels={range.labels} index={opsIndex} />
                ) : opsTab === 'sorter' ? (
                    <SorterPanel rows={sorterHours} devices={sorterDevices} labels={range.labels} hub={hub} />
                ) : (
                    <AccountTable rows={opsAccounts} hub={hub} customer={customer} labels={range.labels} />
                )}
            </div>
        </div>
    );
};

export default OpsSection;
